<script setup lang="ts">
import logger from "@/composables/logger"
import { cookie } from "@/composables/utilities"
import { XIcon } from "@heroicons/vue/solid"
import { computed, ref } from "vue"

const props = withDefaults(
  defineProps<{
    /**
     * Unique id for the banner.
     * Used when setting cookies for dissmissal.
     * Can be modified to short circuit cookie duration.
     */
    id: string
    /**
     * Call to action button text.
     */
    btnText: string
    /**
     * The secondary copy in the banner.
     * Displayed as normal copy after the title.
     */
    description?: string
    /**
     * URL the banner call to action should point to.
     */
    link: string
    /**
     * The time in days the banner should stay hidden once dismissed.
     * Set to 0 to make the banner 'sticky' and prevent the user from dissmissing.
     */
    hideDuration: number
    /**
     * The primary title copy in the banner.
     * Displayed as bold text.
     */
    title: string
  }>(),
  {
    description: "",
  }
)

const isVisible = ref<boolean>(
  props.hideDuration === 0 || cookie.get(props.id) === ""
)

const onDismiss = () => {
  cookie.set(props.id, 1, props.hideDuration)
  isVisible.value = false
}

const isExternalLink = computed(() => {
  if (props.link.charAt(0) === "/") {
    return false
  }

  try {
    const url = new URL(props.link)

    if (url.origin === window.location.origin) {
      return false
    }

    return true
  } catch (e) {
    logger.error("Invalid link property, could not be parsed as URL.", {
      link: props.link,
    })

    return false
  }
})
</script>

<template>
  <div
    v-if="isVisible"
    class="flex items-center justify-center gap-x-6 bg-xy-green py-3 px-6 sm:px-3.5"
    :class="{
      'sm:before:flex-1': hideDuration > 0,
    }"
  >
    <div class="flex flex-wrap items-center gap-y-2 gap-x-4">
      <a :href="link" :target="isExternalLink ? '_blank' : '_self'">
        <p class="text-sm leading-5 text-xy-black font-display">
          <span class="font-semibold">{{ title }}</span>
          <svg
            viewBox="0 0 2 2"
            class="mx-2 inline h-1 w-1 fill-current"
            aria-hidden="true"
          >
            <circle cx="1" cy="1" r="1" /></svg
          >{{ description }}

          <span class="whitespace-nowrap font-semibold text-xy-black"
            >{{ btnText }}&nbsp;<span aria-hidden="true">&rarr;</span></span
          >
        </p>
      </a>
    </div>

    <div v-if="hideDuration > 0" class="flex flex-1 justify-end">
      <button
        type="button"
        class="-m-3 p-3 focus-visible:outline-offset-[-4px]"
        @click="onDismiss"
      >
        <span class="sr-only">Dismiss</span>
        <XIcon class="h-5 w-5 text-xy-black" aria-hidden="true" />
      </button>
    </div>
  </div>
</template>
